<script lang="ts">
let videoPlayerReloadQueue = Promise.resolve()

const queueVideoPlayerReload = () => {
  if (!window.reloadVideoPlayer) return

  videoPlayerReloadQueue = videoPlayerReloadQueue.then(async () => {
    if (!document.querySelector('.bdu-player')) return
    await window.reloadVideoPlayer?.()
  })
}
</script>

<script setup lang="ts">
// TODO: fix styles for placeholder image

import { storeToRefs } from 'pinia'
import { useHead } from '@unhead/vue'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import { ArticleType, VideoLayoutSize } from '@xcel-next/core'

import { usePageStore } from '../../stores/page'
import { useConfigStore } from '../../stores/config'
import { useSourcepointStore } from '../../stores/sourcepoint'
import { useSessionStore } from '../../stores/session'
import EventBus from '../../event-bus/EventBus'
import { useImgSrcSet } from '../../composables/use-img-src-set'
import { useClient } from '../../utils/client-only'
import { useLazyLoadingUtils } from '../../composables/use-lazy-loading-utils'
import { useDeviceDetection } from '../../composables/use-device-detection'

import type { BxMbWindow } from '../../types'
import type { VideoParagraph } from '../../utils/paragraph-schema/paragraphs'

type VideoMoltenBundleWindow = BxMbWindow & {
  bxMb: BxMbWindow['bxMb'] & { dispatchAdTargetingString: (adTargetingString: unknown) => void },
  MoltenBundle: BxMbWindow['MoltenBundle'] & { Video: { getSXPKeysAsync: () => Promise<unknown> } }
}

const props = withDefaults(defineProps<{
  video: VideoParagraph['video'],
  listOrderPosition?: number,
  dataIsSliderChild?: boolean,
  dataSliderInitialSlide?: boolean,
  dataSliderPlayerActive?: boolean,
  includeDepth?: number,
  isPreview?: boolean
}>(), {
  listOrderPosition: 0,
  dataIsSliderChild: false,
  dataSliderInitialSlide: false,
  dataSliderPlayerActive: true,
  includeDepth: 0,
  isPreview: false
})

const { isClient } = useClient()
const { hasMobileAds } = useDeviceDetection()
const { createImgSrc } = useImgSrcSet()
const { getLoadingValue } = useLazyLoadingUtils(props)

const { rsConfig } = storeToRefs(useConfigStore())
const { pageData } = storeToRefs(usePageStore())
const { hasPurSub } = storeToRefs(useSessionStore())
const { consentString, consentsExist, noScripts } = storeToRefs(useSourcepointStore())

const videoWrapperEl = ref<HTMLDivElement | null>(null)
const intervalForJwConfig = ref<ReturnType<typeof setInterval> | null>(null)
const timeoutId = ref<ReturnType<typeof setTimeout> | null>(null)
const scriptInjected = ref(false)
const adTargetingStringDispatchedToPlayer = ref(false)
const fullscreenListenerAttached = ref(false)

const videoProvider = computed(() => props.video?.videoProvider)
const thumbnail = computed(() => props.video?.thumbnail)
const autoPlay = computed(() => props.video?.autoPlay)
const showRecos = computed(() => props.video?.showRecos)
const layout = computed(() => props.video?.layout)
const allowAds = computed(() => props.video?.allowAds)
const capiId = computed(() => props.video?.capiId)
const videoEnvironment = process.env.ENVIRONMENT

const placeholderSrc = computed(() => {
  if (!thumbnail.value) return false

  const imgSrc = createImgSrc({
    image: thumbnail.value,
    type: 'webp',
    brand: 'all'
  })
  return imgSrc
    ? imgSrc.replace(/(w=[0-9]+)/gm, 'w=16')
    : false
})

const autostart = computed(() =>
  // all other autostart logic is handled by the video player itself
  // video articles should always autostart
  (pageData.value?.pageMeta?.articleType === ArticleType.video || autoPlay.value) && !props.isPreview
)

const shouldLoadJwScriptAndRenderPlayer = computed(() => {
  if (props.isPreview) return true

  // client-only + take consent string existence also into consideration so that vast ad calls in the video player are secured with it
  //  NXT-9321 users with pur subscription receive videos without ads
  return isClient.value && !scriptInjected.value && (hasPurSub.value || (consentsExist.value && consentString.value && !noScripts.value))
})

const shouldRenderPlayer = computed(() =>
  shouldLoadJwScriptAndRenderPlayer.value && (!props.dataIsSliderChild || props.dataSliderPlayerActive)
)

const showRecommendations = computed(() => showRecos.value || hasMobileAds.value)

const relatedoverlaymode = computed(() =>
  (rsConfig.value.videoPlayer?.relatedOverlayModeEnabled || hasMobileAds.value) ? 'autoplay' : ''
)

useHead({
  script: computed(() => (!import.meta.env.SSR && shouldLoadJwScriptAndRenderPlayer.value)
    ? [{
      src: process.env.VIDEO_SCRIPT_URI,
      async: true,
      defer: true,
      'data-vmid': 'video-player',
      key: 'video-player'
    }]
    : [])
})

const clearJwConfigInterval = () => {
  if (intervalForJwConfig.value) clearInterval(intervalForJwConfig.value)
  if (timeoutId.value) clearTimeout(timeoutId.value)
}

const removeJwPlayer = () => {
  const jwEl = videoWrapperEl.value?.querySelector<HTMLElement>('.jwplayer')
  if (!jwEl || !window.jwplayer) return

  try {
    window.jwplayer(jwEl.id).remove()
  } catch (err) {
    console.warn(`Could not remove ${jwEl.id}:`, err)
  }
  fullscreenListenerAttached.value = false
}

// Attaches fullscreen event listener to jwPlayer instance and sets up event handling.
const attachFullscreenListenerToJwPlayer = () => {
  if (!shouldRenderPlayer.value || fullscreenListenerAttached.value) return
  // This will get replaced by jwPlayer instance in the DOM once the player is loaded
  const jwEl = videoWrapperEl.value?.firstChild as HTMLElement | null
  // Player is ready when it has the jwplayer class
  if (!jwEl || !jwEl.classList.contains('jwplayer') || fullscreenListenerAttached.value) return
  // Attach listener
  if (window.jwplayer) {
    try {
      window.jwplayer(jwEl.id).on('fullscreen', (event) => {
        EventBus.$emit('toggleAGF', event.fullscreen)
      })
      fullscreenListenerAttached.value = true
    } catch (err) {
      console.warn(`Could not attach fullscreen listener to ${jwEl.id}:`, err)
    }
  }
}

const fetchAndDispatchAdTargetingStringToPlayer = async () => {
  // no molten bundle fun without consent
  if (!shouldLoadJwScriptAndRenderPlayer.value || adTargetingStringDispatchedToPlayer.value) return

  // fetch ad targeting string from molten bundle and dispatch to player
  const bxWindow = window as unknown as VideoMoltenBundleWindow
  if (bxWindow.bxMb) {
    bxWindow.bxMb.dispatchAdTargetingString = (adTargetingString) => {
      const event = new CustomEvent('renderingMoltenBundleReady', { detail: { adTargetingString } })
      document.dispatchEvent(event)
    }

    bxWindow.MoltenBundle = bxWindow.MoltenBundle || {}
    bxWindow.MoltenBundle.cmd = bxWindow.MoltenBundle.cmd || []
    bxWindow.MoltenBundle.cmd.push(async () => {
      try {
        const sxpKeys = await bxWindow.MoltenBundle.Video.getSXPKeysAsync()
        bxWindow.bxMb.dispatchAdTargetingString(sxpKeys)
      } catch (e) {
        console.warn('error fetching molten bundle video sxp keys:', e)
      }
    })
    adTargetingStringDispatchedToPlayer.value = true
  }
}

watch(shouldLoadJwScriptAndRenderPlayer, async (newValue, oldValue) => {
  if (newValue && oldValue === false) {
    await fetchAndDispatchAdTargetingStringToPlayer()
  }
})

const startJwConfigPolling = () => {
  clearJwConfigInterval()
  intervalForJwConfig.value = setInterval(async () => {
    attachFullscreenListenerToJwPlayer()
    await fetchAndDispatchAdTargetingStringToPlayer()
    if (adTargetingStringDispatchedToPlayer.value && fullscreenListenerAttached.value) {
      clearJwConfigInterval()
    }
  }, 100)
  timeoutId.value = setTimeout(() => { clearJwConfigInterval() }, 30000)
}

watch(shouldRenderPlayer, async (shouldRender) => {
  if (!shouldRender) {
    clearJwConfigInterval()
    removeJwPlayer()
    return
  }

  await nextTick()
  queueVideoPlayerReload()
  startJwConfigPolling()
}, { flush: 'pre' })

onMounted(async () => {
  if (!shouldRenderPlayer.value) return

  // re-trigger replacement of placeholder divs with jw instances, since its usually just done on pageload
  await nextTick()
  queueVideoPlayerReload()

  // molten bundle init takes a little longer for the new dynamic ad grid integration.
  // so we interval fetching and dispatching of the ad targeting string to the video
  // player until the molten bundle is ready or interval has timed out (30 sec).
  // Additionally, we attach the fullscreen listener to the jw player in the same interval, since the player might also not be ready yet when the component is mounted.
  startJwConfigPolling()
})

onBeforeUnmount(() => {
  clearJwConfigInterval()
  removeJwPlayer()
})
</script>

<template>
  <div class="bx-content bx-content__media-video bx-typo--content-media"
       :data-tc="`video-${layout?.size}`"
       :data-video-id="capiId">
    <div class="bx-content__media-video-placeholder">
      <img v-if="placeholderSrc"
           :src="placeholderSrc"
           :width="thumbnail?.width"
           :height="thumbnail?.height"
           :loading="getLoadingValue"
           title="Video Platzhalter"
           alt="Video Platzhalter">
    </div>
    <div ref="videoWrapperEl"
         class="bx-content__media-video-wrapper">
      <div v-if="shouldRenderPlayer"
           class="bdu-widget bdu-player"
           data-bdu="bdu"
           :data-bdu-environment="videoEnvironment"
           :data-bdu-consentstring="consentString"
           :data-bdu-allowads="allowAds && !hasPurSub"
           :data-bdu-showrecommendations="showRecommendations"
           :data-bdu-relatedoverlaymode="relatedoverlaymode"
           :data-bdu-autostart="autostart?.toString()"
           :data-bdu-adzone="pageData?.metaData?.adAAdZone"
           :data-bdu-capi-id="capiId" />
    </div>
    <span v-if="videoProvider && videoProvider.length > 0"
          class="bx-content__media-credit bx-content__media-video-credit"
          data-tc="video-credit">
      Video: {{ videoProvider }}
    </span>
  </div>
</template>
